import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import type { PreviewLayoutMode } from "../print-cards.interface";

const CM_TO_PX = 37.795275591;
const PAGE_GAP_PX = 12;
const STAGE_PAD_X = 32;
const STAGE_PAD_Y = 32;
const PREVIEW_READY_TIMEOUT_MS = 5000;
const PREVIEW_READY_MESSAGE = "pc-print-card-ready";

@Component({
  selector: "app-print-card-preview",
  templateUrl: "./print-card-preview.component.html",
  styleUrls: ["./print-card-preview.component.scss"],
  standalone: false,
})
export class PrintCardPreviewComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() frontDocumentHtml: string | null = null;
  @Input() backDocumentHtml: string | null = null;
  @Input() loading = false;
  @Input() pageWidthCm: number | null = null;
  @Input() pageHeightCm: number | null = null;

  @ViewChild("stage") stageRef?: ElementRef<HTMLElement>;

  safeFrontDocumentHtml: SafeHtml | null = null;
  safeBackDocumentHtml: SafeHtml | null = null;
  frontIframeKey = 0;
  backIframeKey = 0;
  displayScale = 1;
  layoutMode: PreviewLayoutMode = "column";
  private contentSettling = false;
  private frontLoadDone = false;
  private backLoadDone = false;
  private expectFrontLoad = false;
  private expectBackLoad = false;
  private settleTimeoutId = 0;
  private resizeObserver?: ResizeObserver;
  private resizeFrameId = 0;
  private settleFrameId = 0;
  private iframeReadyCleanups = new Map<"front" | "back", () => void>();

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly sanitizer: DomSanitizer,
  ) {}

  get hasValidPageSize(): boolean {
    return (
      typeof this.pageWidthCm === "number" &&
      typeof this.pageHeightCm === "number" &&
      Number.isFinite(this.pageWidthCm) &&
      Number.isFinite(this.pageHeightCm) &&
      this.pageWidthCm > 0 &&
      this.pageHeightCm > 0
    );
  }

  get cardWidthPx(): number {
    const widthCm = this.pageWidthCm;
    if (
      typeof widthCm !== "number" ||
      !Number.isFinite(widthCm) ||
      widthCm <= 0
    ) {
      return 1;
    }
    return Math.max(1, Math.round(widthCm * CM_TO_PX));
  }

  get cardHeightPx(): number {
    const heightCm = this.pageHeightCm;
    if (
      typeof heightCm !== "number" ||
      !Number.isFinite(heightCm) ||
      heightCm <= 0
    ) {
      return 1;
    }
    return Math.max(1, Math.round(heightCm * CM_TO_PX));
  }

  get bundleWidthPx(): number {
    if (this.usesRowLayout) {
      return this.cardWidthPx * 2 + PAGE_GAP_PX;
    }
    return this.cardWidthPx;
  }

  get bundleHeightPx(): number {
    if (this.usesRowLayout) {
      return this.cardHeightPx;
    }
    const pageCount = this.previewPageCount;
    return this.cardHeightPx * pageCount + PAGE_GAP_PX * (pageCount - 1);
  }

  get scalerTransform(): string {
    return `scale(${this.displayScale})`;
  }

  get usesRowLayout(): boolean {
    return this.layoutMode === "row" && this.hasFrontSlot && this.hasBackSlot;
  }

  get previewPageCount(): number {
    return (this.hasFrontSlot ? 1 : 0) + (this.hasBackSlot ? 1 : 0) || 1;
  }

  get showLoader(): boolean {
    return this.loading || this.contentSettling;
  }

  get showLoadingOnly(): boolean {
    return this.showLoader && !this.hasFrontDocument && !this.hasBackDocument;
  }

  get showPreviewBundle(): boolean {
    return (
      this.hasValidPageSize && (this.hasFrontDocument || this.hasBackDocument)
    );
  }

  get hasFrontSlot(): boolean {
    return this.showPreviewBundle;
  }

  get hasBackSlot(): boolean {
    return this.showPreviewBundle;
  }

  get hasFrontDocument(): boolean {
    return Boolean(this.safeFrontDocumentHtml);
  }

  get hasBackDocument(): boolean {
    return Boolean(this.safeBackDocumentHtml);
  }

  ngAfterViewInit(): void {
    this.bindResizeObserver();
    this.scheduleUpdateScale();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const htmlChanged =
      Boolean(changes["frontDocumentHtml"]) ||
      Boolean(changes["backDocumentHtml"]);
    const sizeChanged =
      Boolean(changes["pageWidthCm"]) || Boolean(changes["pageHeightCm"]);

    if (htmlChanged) {
      this.syncSafeDocumentHtml();
      this.beginContentSettling();
    }

    if (htmlChanged || sizeChanged || changes["loading"]) {
      this.scheduleUpdateScale();
    }
  }

  ngOnDestroy(): void {
    if (this.resizeFrameId) {
      cancelAnimationFrame(this.resizeFrameId);
    }
    if (this.settleFrameId) {
      cancelAnimationFrame(this.settleFrameId);
    }
    if (this.settleTimeoutId) {
      window.clearTimeout(this.settleTimeoutId);
    }
    this.clearIframeReadyWaiters();
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
  }

  onIframeLoad(side: "front" | "back", event: Event): void {
    if (!this.contentSettling) {
      return;
    }
    if (side === "front") {
      if (!this.expectFrontLoad || this.frontLoadDone) {
        return;
      }
    } else if (!this.expectBackLoad || this.backLoadDone) {
      return;
    }
    const iframe = event.target;
    if (!(iframe instanceof HTMLIFrameElement)) {
      this.markIframeSideReady(side);
      return;
    }
    this.waitForIframeContentReady(side, iframe);
  }

  private syncSafeDocumentHtml(): void {
    const frontHtml = this.frontDocumentHtml?.trim() || "";
    const backHtml = this.backDocumentHtml?.trim() || "";

    this.safeFrontDocumentHtml = frontHtml.length
      ? this.sanitizer.bypassSecurityTrustHtml(frontHtml)
      : null;
    this.safeBackDocumentHtml = backHtml.length
      ? this.sanitizer.bypassSecurityTrustHtml(backHtml)
      : null;

    this.frontIframeKey += 1;
    this.backIframeKey += 1;
  }

  private beginContentSettling(): void {
    if (this.loading || (!this.hasFrontDocument && !this.hasBackDocument)) {
      this.resetContentSettling();
      return;
    }
    this.expectFrontLoad = this.hasFrontDocument;
    this.expectBackLoad = this.hasBackDocument;
    if (!this.expectFrontLoad && !this.expectBackLoad) {
      this.resetContentSettling();
      return;
    }
    this.contentSettling = true;
    this.frontLoadDone = false;
    this.backLoadDone = false;
    this.clearIframeReadyWaiters();
    if (this.settleTimeoutId) {
      window.clearTimeout(this.settleTimeoutId);
    }
    this.settleTimeoutId = window.setTimeout(() => {
      this.settleTimeoutId = 0;
      if (!this.contentSettling) {
        return;
      }
      this.contentSettling = false;
      this.clearIframeReadyWaiters();
      this.scheduleUpdateScale();
      this.cdr.markForCheck();
    }, PREVIEW_READY_TIMEOUT_MS);
    if (this.settleFrameId) {
      cancelAnimationFrame(this.settleFrameId);
    }
    this.settleFrameId = requestAnimationFrame(() => {
      this.settleFrameId = 0;
      this.cdr.markForCheck();
    });
  }

  private waitForIframeContentReady(
    side: "front" | "back",
    iframe: HTMLIFrameElement,
  ): void {
    this.clearIframeReadyWaiter(side);

    const finish = (): void => {
      this.clearIframeReadyWaiter(side);
      this.markIframeSideReady(side);
    };

    try {
      const win = iframe.contentWindow as
        (Window & { EuroPDF?: { readyToRender?: () => boolean } }) | null;
      if (win?.EuroPDF?.readyToRender?.()) {
        finish();
        return;
      }
    } catch {
      finish();
      return;
    }

    const onMessage = (event: MessageEvent): void => {
      if (event.source !== iframe.contentWindow) {
        return;
      }
      const data = event.data as { type?: string } | null;
      if (!data || data.type !== PREVIEW_READY_MESSAGE) {
        return;
      }
      this.ngZone.run(finish);
    };

    window.addEventListener("message", onMessage);
    const timeoutId = window.setTimeout(() => {
      this.ngZone.run(finish);
    }, PREVIEW_READY_TIMEOUT_MS);

    this.iframeReadyCleanups.set(side, () => {
      window.removeEventListener("message", onMessage);
      window.clearTimeout(timeoutId);
    });
  }

  private markIframeSideReady(side: "front" | "back"): void {
    if (side === "front") {
      if (!this.expectFrontLoad || this.frontLoadDone) {
        return;
      }
      this.frontLoadDone = true;
    } else {
      if (!this.expectBackLoad || this.backLoadDone) {
        return;
      }
      this.backLoadDone = true;
    }
    this.finishContentSettlingIfReady();
  }

  private finishContentSettlingIfReady(): void {
    const frontReady = !this.expectFrontLoad || this.frontLoadDone;
    const backReady = !this.expectBackLoad || this.backLoadDone;
    if (!frontReady || !backReady) {
      return;
    }
    this.contentSettling = false;
    if (this.settleTimeoutId) {
      window.clearTimeout(this.settleTimeoutId);
      this.settleTimeoutId = 0;
    }
    this.clearIframeReadyWaiters();
    this.scheduleUpdateScale();
    this.cdr.markForCheck();
  }

  private resetContentSettling(): void {
    this.contentSettling = false;
    this.frontLoadDone = false;
    this.backLoadDone = false;
    this.expectFrontLoad = false;
    this.expectBackLoad = false;
    if (this.settleTimeoutId) {
      window.clearTimeout(this.settleTimeoutId);
      this.settleTimeoutId = 0;
    }
    this.clearIframeReadyWaiters();
  }

  private clearIframeReadyWaiter(side: "front" | "back"): void {
    const cleanup = this.iframeReadyCleanups.get(side);
    if (!cleanup) {
      return;
    }
    cleanup();
    this.iframeReadyCleanups.delete(side);
  }

  private clearIframeReadyWaiters(): void {
    for (const side of [...this.iframeReadyCleanups.keys()]) {
      this.clearIframeReadyWaiter(side);
    }
  }

  private scheduleUpdateScale(): void {
    if (this.resizeFrameId) {
      return;
    }
    this.resizeFrameId = requestAnimationFrame(() => {
      this.resizeFrameId = 0;
      this.updateScale();
    });
  }

  private bindResizeObserver(): void {
    const stage = this.stageRef?.nativeElement;
    if (
      !stage ||
      typeof ResizeObserver === "undefined" ||
      this.resizeObserver
    ) {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => {
      this.ngZone.runOutsideAngular(() => {
        this.scheduleUpdateScale();
      });
    });
    this.resizeObserver.observe(stage);
  }

  private updateScale(): void {
    const stage = this.stageRef?.nativeElement;
    if (!stage) {
      return;
    }

    const stageWidth = stage.clientWidth;
    const stageHeight = stage.clientHeight;
    if (stageWidth < 1 || stageHeight < 1) {
      return;
    }

    const availW = Math.max(1, stageWidth - STAGE_PAD_X);
    const availH = Math.max(1, stageHeight - STAGE_PAD_Y);
    const hasBoth = this.hasFrontSlot && this.hasBackSlot;
    const pageCount = this.previewPageCount;

    let nextLayout: PreviewLayoutMode = "column";
    if (hasBoth) {
      const rowScale = Math.min(
        1,
        availW / (this.cardWidthPx * 2 + PAGE_GAP_PX),
        availH / this.cardHeightPx,
      );
      const columnScale = Math.min(
        1,
        availW / this.cardWidthPx,
        availH / (this.cardHeightPx * 2 + PAGE_GAP_PX),
      );
      nextLayout = rowScale >= columnScale ? "row" : "column";
    }

    const bundleW =
      nextLayout === "row" && hasBoth
        ? this.cardWidthPx * 2 + PAGE_GAP_PX
        : this.cardWidthPx;
    const bundleH =
      nextLayout === "row" && hasBoth
        ? this.cardHeightPx
        : this.cardHeightPx * pageCount + PAGE_GAP_PX * (pageCount - 1);

    const nextScale = Math.max(
      0.15,
      Math.round(Math.min(1, availW / bundleW, availH / bundleH) * 1000) / 1000,
    );

    if (nextScale === this.displayScale && nextLayout === this.layoutMode) {
      return;
    }

    this.displayScale = nextScale;
    this.layoutMode = nextLayout;
    this.ngZone.run(() => this.cdr.markForCheck());
  }
}
